import { useState, useEffect, useCallback } from 'react';
import { useEditor, EditorContent, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import type { NodeViewProps, JSONContent } from '@tiptap/react';
import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import Placeholder from '@tiptap/extension-placeholder';
import { Node, mergeAttributes } from '@tiptap/core';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Loader2, RotateCcw, Mail, MessageSquare } from 'lucide-react';
import type { TransactionalTemplate, TransactionalTemplateVariable } from '@/lib/api';

// Custom Tiptap extension for message variables
const MessageVariable = Node.create({
  name: 'messageVariable',
  group: 'inline',
  inline: true,
  atom: true,

  addAttributes() {
    return {
      key: { default: '' },
      label: { default: '' },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-message-variable]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes, {
      'data-message-variable': HTMLAttributes.key,
      'class': 'message-variable',
    }), `{{${HTMLAttributes.key}}}`];
  },

  addNodeView() {
    return ReactNodeViewRenderer(MessageVariableView);
  },
});

// React component for rendering message variables in the editor
function MessageVariableView({ node }: NodeViewProps) {
  return (
    <NodeViewWrapper as="span" className="inline">
      <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 text-xs font-medium border border-blue-200">
        {node.attrs.label || node.attrs.key}
      </span>
    </NodeViewWrapper>
  );
}

// Convert plain text with {{variables}} to Tiptap JSON
function textToTiptapDoc(text: string, variables: TransactionalTemplateVariable[]): JSONContent {
  const varMap = new Map(variables.map(v => [v.key, v.label]));
  const lines = text.split('\n');

  const content: JSONContent[] = lines.map(line => {
    if (!line) {
      return { type: 'paragraph' };
    }

    const parts = line.split(/({{[^}]+}})/g);
    const nodeContent: JSONContent[] = [];

    for (const part of parts) {
      if (!part) continue;

      const match = part.match(/{{([^}]+)}}/);
      if (match) {
        const key = match[1];
        nodeContent.push({
          type: 'messageVariable',
          attrs: { key, label: varMap.get(key) || key },
        });
      } else {
        nodeContent.push({ type: 'text', text: part });
      }
    }

    return {
      type: 'paragraph',
      content: nodeContent.length > 0 ? nodeContent : undefined,
    };
  });

  return {
    type: 'doc',
    content: content.length > 0 ? content : [{ type: 'paragraph' }],
  };
}

// Convert Tiptap JSON back to plain text
function tiptapDocToText(doc: JSONContent): string {
  function nodeToText(node: JSONContent): string {
    if (node.type === 'text') return node.text || '';
    if (node.type === 'messageVariable') return `{{${node.attrs?.key}}}`;
    if (node.type === 'paragraph') {
      const text = (node.content || []).map(nodeToText).join('');
      return text;
    }
    if (node.type === 'doc') {
      return (node.content || []).map(nodeToText).join('\n');
    }
    return (node.content || []).map(nodeToText).join('');
  }
  return nodeToText(doc);
}

interface MessageTemplateEditorProps {
  template: TransactionalTemplate;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (subject: string, body: string) => Promise<void>;
  onReset: () => Promise<void>;
}

export function MessageTemplateEditor({
  template,
  open,
  onOpenChange,
  onSave,
  onReset,
}: MessageTemplateEditorProps) {
  const [subject, setSubject] = useState(template.subject);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const SMS_MAX_LENGTH = 280;

  const editor = useEditor({
    extensions: [
      Document,
      Paragraph,
      Text,
      MessageVariable,
      Placeholder.configure({
        placeholder: 'Enter your message...',
      }),
    ],
    content: textToTiptapDoc(template.body, template.available_variables),
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[200px] p-3',
      },
    },
    onUpdate: ({ editor }) => {
      const text = tiptapDocToText(editor.getJSON());
      setCharCount(text.length);
    },
  });

  // Reset editor content when template changes
  useEffect(() => {
    if (editor && template) {
      setSubject(template.subject);
      const content = textToTiptapDoc(template.body, template.available_variables);
      editor.commands.setContent(content);
      setCharCount(template.body.length);
    }
  }, [template, editor]);

  const insertVariable = useCallback((variable: TransactionalTemplateVariable) => {
    if (!editor) return;

    editor.chain().focus().insertContent({
      type: 'messageVariable',
      attrs: { key: variable.key, label: variable.label },
    }).run();
  }, [editor]);

  const handleSave = async () => {
    if (!editor) return;

    setSaving(true);
    const body = tiptapDocToText(editor.getJSON());
    await onSave(subject, body);
    setSaving(false);
  };

  const handleReset = async () => {
    setResetting(true);
    await onReset();
    setResetting(false);
  };

  const isOverLimit = template.medium === 'sms' && charCount > SMS_MAX_LENGTH;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
              template.medium === 'email'
                ? 'bg-blue-100 text-blue-600'
                : 'bg-green-100 text-green-600'
            }`}>
              {template.medium === 'email' ? (
                <Mail className="h-5 w-5" />
              ) : (
                <MessageSquare className="h-5 w-5" />
              )}
            </div>
            <div>
              <DialogTitle className="flex items-center gap-2">
                {template.purpose_label}
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                  template.medium === 'email'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-green-100 text-green-700'
                }`}>
                  {template.medium === 'email' ? 'Email' : 'SMS'}
                </span>
              </DialogTitle>
              <DialogDescription className="mt-1">
                Customize the {template.medium === 'email' ? 'email' : 'text message'} sent to your clients.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-4">
          {/* Variables */}
          <div>
            <Label className="text-xs text-muted-foreground mb-2 block">
              Available Variables
            </Label>
            <div className="flex flex-wrap gap-1.5">
              {template.available_variables.map(variable => (
                <button
                  key={variable.key}
                  type="button"
                  onClick={() => insertVariable(variable)}
                  className="inline-flex items-center px-2 py-1 rounded-md bg-blue-50 text-blue-700 text-xs font-medium hover:bg-blue-100 transition-colors border border-blue-200"
                >
                  {variable.label}
                </button>
              ))}
            </div>
          </div>

          {/* Subject (email only) */}
          {template.medium === 'email' && (
            <div className="space-y-2">
              <Label htmlFor="subject">Subject</Label>
              <Input
                id="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Email subject line"
              />
            </div>
          )}

          {/* Body Editor */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Message Body</Label>
              {template.medium === 'sms' && (
                <span className={`text-xs ${
                  isOverLimit ? 'text-destructive font-medium' : 'text-muted-foreground'
                }`}>
                  {charCount} / {SMS_MAX_LENGTH}
                </span>
              )}
            </div>
            <div className={`border rounded-md overflow-hidden ${
              isOverLimit ? 'border-destructive' : ''
            }`}>
              <EditorContent editor={editor} />
            </div>
            {template.medium === 'sms' && isOverLimit && (
              <p className="text-xs text-destructive">
                Message exceeds {SMS_MAX_LENGTH} character limit. Consider shortening your message.
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="flex-shrink-0">
          <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-between">
            <div>
              {template.is_custom && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleReset}
                  disabled={saving || resetting}
                >
                  {resetting ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <RotateCcw className="mr-2 h-4 w-4" />
                  )}
                  Reset to Default
                </Button>
              )}
            </div>
            <div className="flex flex-col-reverse gap-2 sm:flex-row">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={saving || resetting}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSave}
                disabled={saving || resetting || isOverLimit}
              >
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
